import { apiClient } from './client';

// Response types
export interface LoginResponse {
  status: string;
  message: string;
  data: {
    is_new_user: boolean;
  };
}

export interface VerifyOtpResponse {
  status: string;
  message: string;
  data: {
    user_id: number;
    phone_number: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    profile_picture: string | null;
    token: string;
  };
}

export interface UserProfileResponse {
  status: string;
  message: string;
  data: {
    user_id: number;
    phone_number: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    profile_picture: string | null;
  };
}

// Cache for profile data
let profileCache: {
  data: UserProfileResponse | null;
  timestamp: number;
} = {
  data: null,
  timestamp: 0
};

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Request the OTP to be sent to a phone number
export const requestLoginOtp = async (phoneNumber: string): Promise<LoginResponse> => {
  try {
    const response = await apiClient.post('/auth/login/', { phone_number: phoneNumber });
    return response.data as LoginResponse;
  } catch (error) {
    console.error('Error requesting login OTP:', error);
    throw error;
  }
};

// Verify the OTP code
export const verifyOtp = async (phoneNumber: string, otpCode: string): Promise<VerifyOtpResponse> => {
  try {
    const response = await apiClient.post('/auth/verify-otp/', { 
      phone_number: phoneNumber,
      otp_code: otpCode
    });
    return response.data as VerifyOtpResponse;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    throw error;
  }
};

// Check if the token is valid by fetching the user profile
export const validateToken = async (skipCache = false): Promise<UserProfileResponse | null> => {
  // Use cached profile if available and not expired
  const now = Date.now();
  if (!skipCache && profileCache.data && (now - profileCache.timestamp < CACHE_DURATION)) {
    return profileCache.data;
  }

  try {
    const response = await apiClient.get('/user/profile/');
    const profileData = response.data as UserProfileResponse;
    
    // Update cache
    profileCache = {
      data: profileData,
      timestamp: now
    };
    
    return profileData;
  } catch (error) {
    console.error('Error validating token:', error);
    // Clear cache on error
    profileCache = { data: null, timestamp: 0 };
    return null;
  }
};

// Google OAuth login - verify Google ID token and get JWT
export const googleLogin = async (idToken: string): Promise<VerifyOtpResponse> => {
  try {
    const response = await apiClient.post('/auth/google-login/', { 
      id_token: idToken
    });
    return response.data as VerifyOtpResponse;
  } catch (error) {
    console.error('Error with Google login:', error);
    throw error;
  }
}; 